// GA4 ecommerce tracking for the checkout app. Mirrors the dataLayer events
// the legacy OXID checkout pushed via ys_tracking (checkout_steps_main.tpl):
//   step 1 basket  → view_cart
//   step 2 user    → begin_checkout
//   step 3 payment → add_shipping_info   (shipping is selected on this step)
//   step 4 order   → add_payment_info    (payment chosen before advancing)
// Pushes are gated on the consent manager being up (m7_cmp: m7cmp:ready).
// The `purchase` event still fires server-side on the OXID thank-you page.
import { store as basketStore } from './basketStore.svelte.js';
import { paymentStore } from './paymentStore.svelte.js';
import { devLog as _devLog } from '../utils/devLog.js';
import { waehrung } from '../utils/waehrung.js';

const devLog = (tag, ...a) => _devLog(`tracking:${tag}`, ...a);

// reference: [utils/waehrung 1]
const CURRENCY = waehrung();

// --- consent gate ----------------------------------------------------------
// m7_cmp fires `m7cmp:ready` once. The checkout app is a deferred module,
// so the manager usually starts after us and we catch the event normally.
// Poll briefly so a pre-mount event isn't lost.
// decision: [1]
const cmpStarted = () => !!(window.M7Cmp && window.M7Cmp.ready);
let consentReady = false;
const consentQueue = [];
function flushConsent() {
    if (consentReady) return;
    consentReady = true;
    while (consentQueue.length) {
        try { consentQueue.shift()(); } catch (e) { devLog('flush:error', e); }
    }
}
if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.addEventListener('m7cmp:ready', flushConsent, { once: true });
    if (cmpStarted()) {
        flushConsent();
    } else {
        let tries = 0;
        const iv = setInterval(() => {
            if (consentReady || cmpStarted() || ++tries > 40) {
                clearInterval(iv);
                if (cmpStarted()) flushConsent();
            }
        }, 250);
    }
}
function whenConsentReady(cb) {
    if (consentReady) cb();
    else consentQueue.push(cb);
}

// --- payload builders ------------------------------------------------------
function buildItems() {
    const items = [];
    for (const item of basketStore.items.values()) {
        items.push({
            item_id: item.id,
            item_name: item.name,
            item_brand: item.brand || '',
            item_category: item.mainCategory || '',
            item_variant: item.variant || '',
            quantity: item.quantity,
            price: item.price,
        });
    }
    return items;
}

function pushEvent(event, extraEcommerce = {}) {
    whenConsentReady(() => {
        if (basketStore.items.size === 0) { devLog('skip:emptyBasket', { event }); return; }
        // Clear the previous ecommerce object first (GA4 recommendation) so
        // stale items don't leak into the next event.
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event,
            ecommerce: {
                currency: CURRENCY,
                value: basketStore.snapshot?.total?.brutto ?? basketStore.price?.brutto ?? 0,
                ...extraEcommerce,
                items: buildItems(),
            },
        });
        devLog('push', { event });
    });
}

// --- public events ---------------------------------------------------------
export function trackViewCart() { pushEvent('view_cart'); }
export function trackBeginCheckout() { pushEvent('begin_checkout'); }
export function trackAddShippingInfo() { pushEvent('add_shipping_info'); }
export function trackAddPaymentInfo() {
    const sel = (paymentStore.paymentMethods ?? []).find(p => p.id === paymentStore.selectedPaymentId);
    pushEvent('add_payment_info', sel ? { payment_type: sel.title } : {});
}

// purchase — only fired on the in-app ThankYou path. The payload is built
// server-side by DynamicOrderController::buildPurchaseTracking (mirrors
// analytics.tpl) and pushed verbatim. When a gateway redirects to the OXID
// thank-you page instead, purchase fires server-side and this is NOT called.
export function trackPurchase(payload) {
    if (!payload || !payload.event) return;
    whenConsentReady(() => {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(payload);
        devLog('push', { event: 'purchase' });
    });
}
